import { isSupportedAsMainImage } from 'shared/domain/document/isSupportedAsMainImage';
import { isUploadStatusSuccess, UploadStatus } from 'shared/types/uploadStatus';
import {
  ImageConvertStatus,
  isImageConvertStatusSuccess,
} from 'shared/domain/imageConvertStatus/imageConvertStatus';
import { AttachmentCounts } from 'shared/domain/issue/issueModel';

/** What the PDF filters a document by, whichever shape it is read from (DTO, stored entity, view). */
export type PdfClassifiableDocument = {
  deleted?: boolean;
  extension: string;
  uploadStatus: UploadStatus;
  imageConvertStatus?: ImageConvertStatus;
  description?: string | null;
};

/** A document whose file data is nested under `data` (a stored entity or a document on view). */
export type DocumentWithFileData = {
  deleted: boolean;
  description?: string | null;
  imageConvertStatus?: ImageConvertStatus;
  data: { extension: string; uploadStatus: UploadStatus } | null;
};

/** The document as the PDF filters it; `undefined` when it has no file data to print. */
export function toPdfClassifiableDocument(
  document: DocumentWithFileData
): PdfClassifiableDocument | undefined {
  if (!document.data) {
    return undefined;
  }
  return {
    deleted: document.deleted,
    description: document.description,
    imageConvertStatus: document.imageConvertStatus,
    extension: document.data.extension,
    uploadStatus: document.data.uploadStatus,
  };
}

export type PdfDocumentCounts = {
  images: number;
  nonImages: number;
  all: number;
  descriptionChars: number;
};

export const emptyPdfDocumentCounts = (): PdfDocumentCounts => ({
  images: 0,
  nonImages: 0,
  all: 0,
  descriptionChars: 0,
});

function isDeleted(document: PdfClassifiableDocument): boolean {
  return Boolean(document.deleted);
}

function isUploadSuccessful(document: PdfClassifiableDocument): boolean {
  return isUploadStatusSuccess(document.uploadStatus);
}

/** Mirrors backend `FilterDocumentsForPdf.isSupportedImage`. */
export function isPdfSupportedImage(document: PdfClassifiableDocument): boolean {
  if (isDeleted(document) || !isUploadSuccessful(document)) {
    return false;
  }
  return (
    isSupportedAsMainImage(document.extension) ||
    isImageConvertStatusSuccess(document.imageConvertStatus)
  );
}

/** Mirrors backend `FilterDocumentsForPdf.isSupportedNonImage`. */
export function isPdfSupportedNonImage(document: PdfClassifiableDocument): boolean {
  if (isDeleted(document) || !isUploadSuccessful(document)) {
    return false;
  }
  return (
    !isSupportedAsMainImage(document.extension) &&
    !isImageConvertStatusSuccess(document.imageConvertStatus)
  );
}

/** Mirrors backend `FilterDocumentsForPdf.isAnySupportedDocument`. */
export function isPdfSupportedDocument(document: PdfClassifiableDocument): boolean {
  return !isDeleted(document) && isUploadSuccessful(document);
}

export function classifyDocumentsForPdfEstimate(
  documents: PdfClassifiableDocument[]
): PdfDocumentCounts {
  const counts = emptyPdfDocumentCounts();

  for (const document of documents) {
    if (!isPdfSupportedDocument(document)) {
      continue;
    }

    counts.all += 1;
    counts.descriptionChars += (document.description ?? '').length;

    if (isPdfSupportedImage(document)) {
      counts.images += 1;
    } else if (isPdfSupportedNonImage(document)) {
      counts.nonImages += 1;
    } else {
      // Defensive fallback: after isPdfSupportedDocument the image/non-image
      // predicates are complementary, so this branch should be unreachable.
      // Count as image to stay conservative for page estimates.
      counts.images += 1;
    }
  }

  return counts;
}

/** The counts of documents known only by their compact counts (no descriptions). */
export function attachmentCountsToPdfCounts(counts: AttachmentCounts | undefined): PdfDocumentCounts {
  if (counts === undefined) {
    return emptyPdfDocumentCounts();
  }
  return {
    images: counts.images,
    nonImages: counts.nonImages,
    all: counts.images + counts.nonImages,
    descriptionChars: 0,
  };
}

/** The compact counts an issue or a message carries instead of its documents. */
export function toAttachmentCounts(documents: PdfClassifiableDocument[]): AttachmentCounts {
  const { images, nonImages } = classifyDocumentsForPdfEstimate(documents);
  return { images, nonImages };
}

export function scalePdfDocumentCounts(
  counts: PdfDocumentCounts,
  totalCount: number
): PdfDocumentCounts {
  if (totalCount <= 0) {
    return emptyPdfDocumentCounts();
  }

  if (counts.all <= 0) {
    return {
      images: totalCount,
      nonImages: 0,
      all: totalCount,
      descriptionChars: counts.descriptionChars,
    };
  }

  if (counts.all === totalCount) {
    return counts;
  }

  const ratio = totalCount / counts.all;
  return {
    images: Math.round(counts.images * ratio),
    nonImages: Math.round(counts.nonImages * ratio),
    all: totalCount,
    descriptionChars: counts.descriptionChars,
  };
}
